import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { AuthModal } from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { api, tl } from "@/lib/api";
import { playPing } from "@/lib/sound";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Bell, Wallet, User, Briefcase, FileText, MessageSquare, Heart, Settings,
  LogOut, Repeat, PhoneCall, Menu, Search, LayoutDashboard, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const NAV_LINKS = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/ilanlar", label: "İş İlanları" },
  { to: "/ustalar", label: "Ustalar" },
  { to: "/blog", label: "Blog" },
  { to: "/hakkimizda", label: "Hakkımızda" },
];

export function Navbar() {
  const { user, logout, setUser, refreshUser } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const seenRef = useRef(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if (!(user && user.id)) return;
    let alive = true;
    const poll = async () => {
      try {
        const [{ data: w }, { data: n }, { data: convs }] = await Promise.all([
          api.get("/wallet"), api.get("/notifications"), api.get("/conversations"),
        ]);
        if (!alive) return;
        setWallet(w); setUnread(n.unread); setNotifs(n.items || []);
        setMsgUnread((convs || []).reduce((s, c) => s + (c.unread || 0), 0));
        const first = seenRef.current.size === 0;
        for (const item of (n.items || [])) {
          if (!seenRef.current.has(item.id)) {
            seenRef.current.add(item.id);
            if (!first && item.type === "new_message" && !item.read) {
              playPing();
              toast(item.title, { description: item.body, action: { label: "Aç", onClick: () => navigate(item.link || "/mesajlar") } });
            }
          }
        }
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [user, navigate]);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const delNotif = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/notifications/${id}`); setNotifs((p) => p.filter((x) => x.id !== id)); } catch { /* ignore */ }
  };
  const delAllNotifs = async () => {
    try { await api.delete("/notifications"); setNotifs([]); setUnread(0); } catch { /* ignore */ }
  };

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden lg:flex items-center gap-6">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-link-${l.label}`}
                className={({ isActive }) =>
                  `relative text-sm font-semibold transition-colors pb-1 ${isActive ? "text-emerald-600 after:absolute after:left-0 after:-bottom-0.5 after:h-0.5 after:w-full after:rounded-full after:bg-emerald-500" : "text-slate-600 hover:text-emerald-600"}`
                }>
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" className="hidden sm:flex text-slate-500" onClick={() => navigate("/ilanlar")} data-testid="nav-search-button">
            <Search className="h-5 w-5" />
          </Button>

          {user && user.id ? (
            <>
              <button data-testid="nav-wallet-balance-pill" onClick={() => navigate("/cuzdanim")}
                className="hidden sm:flex items-center gap-2 h-9 pl-3 pr-4 rounded-full tinted text-emerald-700 font-semibold text-sm hover:bg-emerald-100 transition-colors">
                <Wallet className="h-4 w-4" />
                {wallet ? tl(wallet.available_balance) : "0 TL"}
              </button>

              <button data-testid="nav-messages-button" onClick={() => navigate("/mesajlar")}
                className="relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600">
                <MessageSquare className="h-5 w-5" />
                {msgUnread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center">{msgUnread}</span>}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-notifications-button"
                    className="relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-emerald-500" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 rounded-2xl p-0">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
                    <span className="font-bold text-slate-900 text-sm">Bildirimler</span>
                    {notifs.length > 0 && <button onClick={delAllNotifs} data-testid="notif-clear-all" className="text-xs font-semibold text-red-500 hover:text-red-600">Tümünü Sil</button>}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifs.length === 0 ? <div className="px-4 py-8 text-center text-sm text-slate-400">Bildirim yok.</div> : notifs.map((n) => (
                      <div key={n.id} data-testid={`nav-notif-${n.id}`} onClick={() => navigate(n.link || "/mesajlar")}
                        className="flex items-start gap-2 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 text-sm truncate">{n.title}</div>
                          <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                        </div>
                        <button onClick={(e) => delNotif(e, n.id)} data-testid={`notif-del-${n.id}`} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild data-testid="nav-profile-dropdown-trigger">
                  <button className="flex items-center gap-2 rounded-full hover:bg-slate-100 p-1 pr-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-bold">{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 rounded-2xl">
                  <DropdownMenuLabel>
                    <div className="font-bold text-slate-900">{user.name}</div>
                    <div className="text-xs text-slate-400">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/hesabim")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" />Profilim</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/cuzdanim")} data-testid="menu-wallet"><Wallet className="h-4 w-4 mr-2" />Cüzdanım & Bakiyem</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/hesabim?tab=jobs")}><Briefcase className="h-4 w-4 mr-2" />İlanlarım</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/hesabim?tab=offers")}><FileText className="h-4 w-4 mr-2" />Tekliflerim</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/hesabim?tab=unlocks")}><PhoneCall className="h-4 w-4 mr-2" />Açtığım İletişimler</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mesajlar")}><MessageSquare className="h-4 w-4 mr-2" />Mesajlarım</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/hesabim?tab=favorites")}><Heart className="h-4 w-4 mr-2" />Favorilerim</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><LayoutDashboard className="h-4 w-4 mr-2" />Admin Paneli</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/hesabim?tab=settings")}><Settings className="h-4 w-4 mr-2" />Ayarlar</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="text-red-600" data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Çıkış</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => navigate("/ilan-ver")} className="hidden md:flex brand-gradient text-white rounded-xl font-semibold" data-testid="nav-post-job-button">
                Ücretsiz İlan Ver
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setAuthOpen(true)} className="rounded-xl font-semibold text-slate-700" data-testid="nav-login-button">Giriş Yap</Button>
              <Button onClick={() => setAuthOpen(true)} className="brand-gradient text-white rounded-xl font-semibold" data-testid="nav-register-button">Üye Ol</Button>
            </>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-slate-600" data-testid="nav-mobile-menu"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} className="py-3 px-2 rounded-lg font-semibold text-slate-700 hover:bg-slate-100">{l.label}</Link>
                ))}
                <Button onClick={() => navigate("/ilan-ver")} className="brand-gradient text-white rounded-xl mt-4">İlan Ver</Button>
                {!user?.id && <Button variant="outline" onClick={() => setAuthOpen(true)} className="rounded-xl">Giriş Yap</Button>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
